import React, { useState, useEffect } from 'react'
import {
    Box,
    Button,
    Card,
    TextField,
    MenuItem,
    Typography,
    Stack,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Checkbox,
    LinearProgress,
    Paper,
} from '@mui/material'
import {
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material'
import {
    useGetBalancingRecommendationsQuery,
    useUpdateReorderPointsMutation,
    useExportBalancingReportMutation,
    BalancingRecommendation,
    BalancingFilters,
    ReorderPointUpdate,
} from '../store/services/inventoryApi'
import { formatCompactCurrency } from '../utils/currency'

const InventoryBalancingMUI: React.FC = () => {
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)

    // Filters
    const [filters, setFilters] = useState<BalancingFilters>({
        stock_status: 'all',
        sales_velocity: 'all',
        confidence_level: 'all',
    })

    // Pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // Selection and bulk operations
    const [selectedVariants, setSelectedVariants] = useState<Set<string>>(new Set())
    const [bulkUpdateDialog, setBulkUpdateDialog] = useState(false)
    const [bulkReorderPoint, setBulkReorderPoint] = useState('')
    const [bulkSafetyMultiplier, setBulkSafetyMultiplier] = useState('')

    // RTK Query hooks
    const { 
        data: balancingData, 
        isLoading: loading, 
        error: queryError,
        refetch 
    } = useGetBalancingRecommendationsQuery({
        filters,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    })

    const [updateReorderPoints, { isLoading: isUpdating }] = useUpdateReorderPointsMutation()
    const [exportReport] = useExportBalancingReportMutation()

    // Extract data from RTK Query response
    const recommendations = balancingData?.recommendations || []
    const summary = balancingData?.summary || {
        total_variants: 0,
        variants_needing_reorder: 0,
        total_suggested_order_value: 0,
        fast_moving_variants: 0,
        slow_moving_variants: 0,
    }
    const totalCount = balancingData?.pagination.total || 0

    // Handle query errors
    useEffect(() => {
        if (queryError) {
            setError('Failed to load inventory balancing recommendations')
            console.error('Error loading recommendations:', queryError)
        }
    }, [queryError])

    // Handle filter changes
    const handleFilterChange = (field: keyof BalancingFilters, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }))
        setPage(0) // Reset to first page when filters change
    }

    // Handle selection
    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = new Set(recommendations.map(r => r.variant_id))
            setSelectedVariants(newSelected)
        } else {
            setSelectedVariants(new Set())
        }
    }

    const handleSelectVariant = (variantId: string) => {
        const newSelected = new Set(selectedVariants)
        if (newSelected.has(variantId)) {
            newSelected.delete(variantId)
        } else {
            newSelected.add(variantId)
        }
        setSelectedVariants(newSelected)
    }

    // Handle bulk update
    const handleBulkUpdate = async () => {
        if (selectedVariants.size === 0) return

        try {
            const updates: ReorderPointUpdate[] = Array.from(selectedVariants).map(variantId => ({
                variant_id: variantId,
                new_reorder_point: parseInt(bulkReorderPoint),
                new_safety_stock_multiplier: bulkSafetyMultiplier ? parseFloat(bulkSafetyMultiplier) : undefined,
            }))

            const response = await updateReorderPoints({ updates }).unwrap()

            if (response.successful_updates > 0) {
                setSuccess(`Successfully updated ${response.successful_updates} reorder points`)
                setBulkUpdateDialog(false)
                setSelectedVariants(new Set())
                setBulkReorderPoint('')
                setBulkSafetyMultiplier('')
                refetch()
            }

            if (response.failed_updates > 0) {
                setError(`${response.failed_updates} updates failed`)
            }
        } catch (err) {
            setError('Failed to update reorder points')
            console.error('Error updating reorder points:', err)
        }
    }

    // Handle export
    const handleExport = async (format: 'excel' | 'csv' = 'excel') => {
        try {
            const blob = await exportReport({ filters, format }).unwrap()
            
            // Create download link
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            
            // Use appropriate file extension based on format
            const fileExtension = format === 'excel' ? 'xlsx' : 'csv'
            const fileName = `inventory_balancing_${new Date().toISOString().split('T')[0]}.${fileExtension}`
            a.download = fileName
            
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
            
            setSuccess(`Report exported successfully as ${fileName}`)
        } catch (err) {
            setError('Failed to export report')
            console.error('Error exporting report:', err)
        }
    }

    // Utility functions
    const getStockStatusColor = (recommendation: BalancingRecommendation) => {
        if (recommendation.available_stock === 0) return 'error'
        if (recommendation.available_stock <= recommendation.minimum_stock) return 'warning'
        if (recommendation.available_stock <= recommendation.reorder_point) return 'info'
        return 'success'
    }

    const getStockStatusText = (recommendation: BalancingRecommendation) => {
        if (recommendation.available_stock === 0) return 'Out of Stock'
        if (recommendation.available_stock <= recommendation.minimum_stock) return 'Low Stock'
        if (recommendation.available_stock <= recommendation.reorder_point) return 'Needs Reorder'
        return 'In Stock'
    }

    const getTrendIcon = (direction: string) => {
        switch (direction) {
            case 'increasing': return <TrendingUpIcon color="success" />
            case 'decreasing': return <TrendingDownIcon color="error" />
            default: return <TrendingFlatIcon color="info" />
        }
    }

    const getVelocityColor = (velocity: string) => {
        switch (velocity) {
            case 'fast': return 'success'
            case 'medium': return 'warning'
            case 'slow': return 'error'
            default: return 'default'
        }
    }

    const getConfidenceColor = (confidence: string) => {
        switch (confidence) {
            case 'high': return 'success'
            case 'medium': return 'warning'
            case 'low': return 'error'
            default: return 'default'
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Enhanced Header with Dhanshika Exports Branding */}
            <Paper 
                sx={{ 
                    p: 3, 
                    mb: 3, 
                    background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 4px 16px rgba(33, 150, 243, 0.3)',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                            Inventory Balancing
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Smart reorder recommendations and stock optimization for Dhanshika Exports
                        </Typography>
                    </Box>
                    
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => refetch()}
                            disabled={loading}
                            size="small"
                            sx={{ 
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExport('excel')}
                            disabled={loading}
                            size="small"
                            sx={{ 
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            Export Excel
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleExport('csv')}
                            disabled={loading}
                            size="small"
                            sx={{ 
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            Export CSV
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {/* Enhanced Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card 
                        sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(33, 150, 243, 0.1)',
                            '&:hover': {
                                boxShadow: '0 4px 16px rgba(33, 150, 243, 0.15)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
                            {summary.total_variants}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Total Variants
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card 
                        sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(255, 152, 0, 0.1)',
                            '&:hover': {
                                boxShadow: '0 4px 16px rgba(255, 152, 0, 0.15)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mb: 1 }}>
                            {summary.variants_needing_reorder}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Need Reorder
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card 
                        sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(33, 150, 243, 0.1)',
                            '&:hover': {
                                boxShadow: '0 4px 16px rgba(33, 150, 243, 0.15)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
                            {formatCompactCurrency(summary.total_suggested_order_value)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Suggested Order Value
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card 
                        sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(76, 175, 80, 0.1)',
                            '&:hover': {
                                boxShadow: '0 4px 16px rgba(76, 175, 80, 0.15)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mb: 1 }}>
                            {summary.fast_moving_variants}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Fast Moving
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card 
                        sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(244, 67, 54, 0.1)',
                            '&:hover': {
                                boxShadow: '0 4px 16px rgba(244, 67, 54, 0.15)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mb: 1 }}>
                            {summary.slow_moving_variants}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Slow Moving
                        </Typography>
                    </Card>
                </Grid>
            </Grid>

            {/* Enhanced Filters */}
            <Card 
                sx={{ 
                    p: 3, 
                    mb: 3,
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.08)',
                }}
            >
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                    Smart Filters
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Stock Status</InputLabel>
                            <Select
                                value={filters.stock_status || 'all'}
                                label="Stock Status"
                                onChange={(e) => handleFilterChange('stock_status', e.target.value)}
                                sx={{
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="low_stock">Low Stock</MenuItem>
                                <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                                <MenuItem value="needs_reorder">Needs Reorder</MenuItem>
                                <MenuItem value="overstocked">Overstocked</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Sales Velocity</InputLabel>
                            <Select
                                value={filters.sales_velocity || 'all'}
                                label="Sales Velocity"
                                onChange={(e) => handleFilterChange('sales_velocity', e.target.value)}
                                sx={{
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="all">All Velocities</MenuItem>
                                <MenuItem value="fast">Fast Moving</MenuItem>
                                <MenuItem value="medium">Medium Moving</MenuItem>
                                <MenuItem value="slow">Slow Moving</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Confidence Level</InputLabel>
                            <Select
                                value={filters.confidence_level || 'all'}
                                label="Confidence Level"
                                onChange={(e) => handleFilterChange('confidence_level', e.target.value)}
                                sx={{
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="all">All Confidence</MenuItem>
                                <MenuItem value="high">High Confidence</MenuItem>
                                <MenuItem value="medium">Medium Confidence</MenuItem>
                                <MenuItem value="low">Low Confidence</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">
                                {totalCount} recommendations found
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            {/* Enhanced Bulk Actions */}
            {selectedVariants.size > 0 && (
                <Card 
                    sx={{ 
                        p: 3, 
                        mb: 3, 
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                        border: '2px solid rgba(33, 150, 243, 0.2)',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip 
                                label={`${selectedVariants.size} selected`}
                                color="primary"
                                variant="filled"
                                sx={{ fontWeight: 600 }}
                            />
                            <Typography variant="body1" fontWeight={500} color="primary.main">
                                Bulk Actions Available
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={() => setBulkUpdateDialog(true)}
                                sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                }}
                            >
                                Update Reorder Points
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setSelectedVariants(new Set())}
                                sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderRadius: 2,
                                }}
                            >
                                Clear Selection
                            </Button>
                        </Stack>
                    </Stack>
                </Card>
            )}

            {/* Loading indicator */}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Enhanced Data Table */}
            <Card 
                sx={{ 
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow 
                                sx={{ 
                                    backgroundColor: '#0d47a1',
                                    '& .MuiTableCell-head': {
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        borderBottom: 'none',
                                    },
                                }}
                            >
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedVariants.size > 0 && selectedVariants.size < recommendations.length}
                                        checked={recommendations.length > 0 && selectedVariants.size === recommendations.length}
                                        onChange={handleSelectAll}
                                        sx={{ 
                                            color: 'rgba(255,255,255,0.7)',
                                            '&.Mui-checked': {
                                                color: 'white',
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: 'white',
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell>Product Details</TableCell>
                                <TableCell align="right">Current Stock</TableCell>
                                <TableCell align="right">Available</TableCell>
                                <TableCell align="right">Min Stock</TableCell>
                                <TableCell align="right">Reorder Point</TableCell>
                                <TableCell align="right">Avg Daily Sales</TableCell>
                                <TableCell align="right">Days Remaining</TableCell>
                                <TableCell align="right">Suggested Order</TableCell>
                                <TableCell>Velocity</TableCell>
                                <TableCell>Trend</TableCell>
                                <TableCell>Confidence</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recommendations.map((recommendation, index) => (
                                <TableRow
                                    key={recommendation.variant_id}
                                    hover
                                    selected={selectedVariants.has(recommendation.variant_id)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.04)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(33, 150, 243, 0.12)',
                                            },
                                        },
                                        borderBottom: index === recommendations.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.06)',
                                    }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedVariants.has(recommendation.variant_id)}
                                            onChange={() => handleSelectVariant(recommendation.variant_id)}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight="medium">
                                                {recommendation.product_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {recommendation.variant_sku} - {recommendation.variant_name}
                                            </Typography>
                                            {recommendation.category_name && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {recommendation.category_name}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {recommendation.current_stock}
                                        </Typography>
                                        {recommendation.reserved_stock > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                ({recommendation.reserved_stock} reserved)
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="medium">
                                            {recommendation.available_stock}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {recommendation.minimum_stock}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {recommendation.reorder_point}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {recommendation.average_daily_sales_30d.toFixed(1)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            (30d avg)
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography
                                            variant="body2"
                                            color={recommendation.days_of_stock_remaining <= 7 ? 'error.main' : 'text.primary'}
                                            fontWeight={recommendation.days_of_stock_remaining <= 7 ? 'bold' : 'normal'}
                                        >
                                            {recommendation.days_of_stock_remaining}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="medium">
                                            {recommendation.suggested_order_quantity}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={recommendation.sales_velocity}
                                            size="small"
                                            color={getVelocityColor(recommendation.sales_velocity) as any}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {getTrendIcon(recommendation.trend_direction)}
                                            <Typography variant="caption">
                                                {recommendation.trend_direction}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={recommendation.confidence_level}
                                            size="small"
                                            color={getConfidenceColor(recommendation.confidence_level) as any}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStockStatusText(recommendation)}
                                            size="small"
                                            color={getStockStatusColor(recommendation) as any}
                                            variant="filled"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10))
                        setPage(0)
                    }}
                />
            </Card>

            {/* Bulk Update Dialog */}
            <Dialog open={bulkUpdateDialog} onClose={() => setBulkUpdateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Reorder Points</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Update reorder points for {selectedVariants.size} selected variant(s)
                    </Typography>
                    <Stack spacing={3}>
                        <TextField
                            label="New Reorder Point"
                            type="number"
                            value={bulkReorderPoint}
                            onChange={(e) => setBulkReorderPoint(e.target.value)}
                            fullWidth
                            required
                            helperText="Set the new reorder point for all selected variants"
                        />
                        <TextField
                            label="Safety Stock Multiplier (Optional)"
                            type="number"
                            value={bulkSafetyMultiplier}
                            onChange={(e) => setBulkSafetyMultiplier(e.target.value)}
                            fullWidth
                            inputProps={{ step: 0.1, min: 1.0, max: 3.0 }}
                            helperText="Multiplier for safety stock calculation (1.0 - 3.0)"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkUpdateDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleBulkUpdate}
                        variant="contained"
                        disabled={!bulkReorderPoint || isUpdating}
                    >
                        {isUpdating ? <CircularProgress size={20} /> : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success/Error Snackbars */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
            >
                <Alert onClose={() => setSuccess(null)} severity="success">
                    {success}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert onClose={() => setError(null)} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default InventoryBalancingMUI